import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass,
  Lightbulb,
  Layers,
  Rocket,
  Sparkles,
  Users,
  GraduationCap,
  Quote,
} from "lucide-react";
import { HERO_BG, FOUNDER_IMG, STUDENTS_IMG, SPEAKER_IMG } from "@/lib/constants";

const framework = [
  {
    label: "01",
    title: "Diagnose",
    body: "Identify the student, leadership or institutional need with rigor — not guesswork.",
    icon: Compass,
  },
  {
    label: "02",
    title: "Decide",
    body: "Translate that information into the most strategic next step for your school.",
    icon: Lightbulb,
  },
  {
    label: "03",
    title: "Design",
    body: "Build the right program, curriculum, system or implementation plan around it.",
    icon: Layers,
  },
  {
    label: "04",
    title: "Deliver",
    body: "Put the solution into practice in a way that produces measurable, defensible value.",
    icon: Rocket,
  },
];

const solutions = [
  {
    tag: "For Students",
    title: "Student Decision Intelligence",
    body: "Diagnostic tools that help schools personalize student support and college-readiness decisions. Flagship product: TestFit®.",
    to: "/testfit",
    cta: "Explore TestFit",
    icon: GraduationCap,
  },
  {
    tag: "For Leaders",
    title: "Leadership Decision Intelligence",
    body: "The GLAD Score™, keynotes and workshops that improve leadership awareness, team communication and institutional decisions.",
    to: "/leadership",
    cta: "Explore Leadership",
    icon: Users,
  },
  {
    tag: "For Institutions",
    title: "Institutional Decision Tools",
    body: "The Helm™ platform, custom decision instruments and the frameworks that let schools stop running on defaults and start running on reads.",
    to: "/curriculum",
    cta: "Explore the Tool Stack",
    icon: Sparkles,
  },
];

const testimonials = [
  {
    quote:
      "TestFit® is awesome. My parents didn't go to school in the US, so it was really helpful to have TestFit® help me choose which test was best for me.",
    name: "Nilah",
    role: "Junior, Miami, FL",
  },
  {
    quote:
      "I hit my target score earlier than expected — all because of TestFit®! I'm done and can focus on other things.",
    name: "Maya",
    role: "Senior, Miami, FL",
  },
  {
    quote:
      "You are amazing. The impact you had on us will be remembered — we'll never forget the 'dreaming child' we had suppressed.",
    name: "T. Barry",
    role: "CEO",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FDFBF7]">
        <div className="absolute inset-0 -z-0">
          <img
            src={HERO_BG}
            alt=""
            className="w-full h-full object-cover object-center opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F4C5C]/70 via-[#0F4C5C]/50 to-[#FDFBF7]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-40 pb-28 md:pb-40">
          <div className="max-w-3xl">
            <div className="overline text-[#FDFBF7]/80 mb-6" data-testid="hero-overline">
              Decision Intelligence for Education
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#FDFBF7]"
            >
              Better decisions <br />
              build <em className="italic text-[#E3B23C]">better schools.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#FDFBF7]/85 leading-relaxed max-w-2xl">
              EdgeTech Global builds decision instruments for schools — diagnostic tools, frameworks and a decision-intelligence platform that help institutions decide before they design, save time and money, and give students, leaders and families a clearer next step.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact?interest=testfit_demo"
                data-testid="hero-cta-primary"
                className="btn-primary bg-[#E3B23C] border-[#E3B23C] text-[#22223B] hover:bg-[#d1a12f]"
              >
                Schedule a Demonstration
                <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/testfit"
                data-testid="hero-cta-secondary"
                className="btn-secondary text-[#FDFBF7] border-[#FDFBF7]/60 hover:bg-white/10"
              >
                Explore Our Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM FRAMING */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <div className="overline">The problem</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4 text-[#22223B]">
              Schools are drowning in information — <em className="italic text-[#0F4C5C]">starving for clarity.</em>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6 text-lg leading-relaxed text-[#22223B]/80">
            <p>
              You don't need another dashboard, another program, or another consultant. You need a way to know — before you decide — what each student, each leader, and each team actually needs.
            </p>
            <p>
              EdgeTech Global builds the instruments that answer that question. Small, precise tools that save your school <strong className="text-[#0F4C5C]">time, energy, attention and money</strong>, and give the humans in your building a saner day.
            </p>
          </div>
        </div>
      </section>

      {/* TESTFIT FEATURE BLOCK */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 relative">
            <div className="aspect-[4/5] w-full overflow-hidden rounded-lg relative">
              <img
                src={STUDENTS_IMG}
                alt="Students preparing for college entrance exams"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 bg-[#FDFBF7] px-4 py-3 rounded-md border border-black/5 shadow-[0_8px_32px_rgba(15,76,92,0.08)]">
                <div className="overline text-[#0F4C5C]">Under 20 minutes</div>
                <div className="font-serif-display text-xl text-[#22223B]">SAT or ACT — clarified.</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="overline text-[#0F4C5C]">Flagship Product</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-3">
              TestFit<sup className="text-lg">®</sup>
            </h2>
            <p className="mt-6 text-lg text-[#22223B]/80 leading-relaxed">
              The right test preparation begins <em className="italic text-[#0F4C5C]">before</em> the preparation starts. TestFit® helps schools determine whether each student is better suited for the SAT or ACT — in less than 20 minutes.
            </p>
            <ul className="mt-6 space-y-3 text-[#22223B]/85">
              {[
                "Give students a clearer starting point for test prep",
                "Provide counselors with a defensible, data-backed recommendation",
                "Stop wasting resources preparing students for the wrong test",
                "Collect cohort-level insight for schoolwide strategy",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#E3B23C]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/testfit" data-testid="home-testfit-cta" className="btn-primary text-sm">
                Explore TestFit <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact?interest=testfit_demo" className="btn-secondary text-sm">
                Request a School Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THREE SOLUTION AREAS */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
            <div className="md:col-span-7">
              <div className="overline">Solutions</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-3">
                Decision intelligence at <em className="italic text-[#0F4C5C]">every level</em> of your school.
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 text-[#22223B]/75 leading-relaxed">
              Three connected areas — one coherent mission. From individual students to leadership teams to institutional systems.
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {solutions.map((s, i) => (
              <div
                key={s.title}
                data-testid={`solution-card-${i}`}
                className="card-soft p-8 flex flex-col"
              >
                <s.icon size={28} strokeWidth={1.4} className="text-[#0F4C5C]" />
                <div className="overline mt-6">{s.tag}</div>
                <h3
                  className="font-serif-display text-2xl mt-3 leading-snug"
                  dangerouslySetInnerHTML={{ __html: s.title }}
                />
                <p className="mt-4 text-[#22223B]/75 leading-relaxed text-[15px]">{s.body}</p>
                <Link
                  to={s.to}
                  className="link-underline mt-6 inline-flex items-center gap-1 self-start text-sm"
                >
                  {s.cta} <ArrowUpRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRAMEWORK — DIAGNOSE DECIDE DESIGN DELIVER */}
      <section className="relative section-pad bg-[#0F4C5C] text-[#FDFBF7] grain overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <div className="overline text-[#E3B23C]">Our method</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                Diagnose. Decide. <em className="italic text-[#E3B23C]">Design.</em> Deliver.
              </h2>
            </div>
            <p className="md:col-span-5 text-[#FDFBF7]/75 leading-relaxed">
              A single, defensible framework that carries schools from information all the way through to implementation.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {framework.map((f) => (
              <div
                key={f.title}
                className="p-8 border border-white/10 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <f.icon size={26} strokeWidth={1.4} className="text-[#E3B23C]" />
                  <span className="overline text-[#FDFBF7]/50">{f.label}</span>
                </div>
                <h3 className="font-serif-display text-2xl mt-6">{f.title}</h3>
                <p className="mt-3 text-sm text-[#FDFBF7]/75 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden bg-[#F0EDE6]">
                <img
                  src={FOUNDER_IMG}
                  alt="Dr. Crystal Gifford, founder of EdgeTech Global"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#0F4C5C] text-[#FDFBF7] px-5 py-4 rounded-md hidden md:block">
                <div className="overline text-[#E3B23C]">Founder &amp; CEO</div>
                <div className="font-serif-display text-lg">Dr. Crystal Gifford</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="overline">Built by an educator</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              An economist and educator who understands the <em className="italic text-[#0F4C5C]">decisions schools face.</em>
            </h2>
            <p className="mt-6 text-lg text-[#22223B]/80 leading-relaxed">
              Dr. Crystal Gifford — "The Conscious Economist" — holds a doctorate in finance and economics with 25+ years as an entrepreneur, educator and speaker. Speaking since 1999, she has energized audiences from private-school campuses to the Carnegie Hall stage. Through EdgeTech Global she combines that experience with technology and strategic design to solve complex institutional challenges. Her throughline: help people make great decisions faster.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about" data-testid="home-about-cta" className="btn-secondary text-sm">
                About EdgeTech Global
              </Link>
              <Link
                to="/speaking"
                className="btn-primary text-sm"
                data-testid="home-speaking-cta"
              >
                Bring Dr. Crystal to Your Organization <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="overline">Field notes</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              What students, leaders and educators are saying.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {testimonials.map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="card-soft p-8 flex flex-col justify-between"
              >
                <Quote size={22} strokeWidth={1.4} className="text-[#E3B23C]" />
                <blockquote className="mt-6 text-[17px] leading-relaxed text-[#22223B]/85">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-8 pt-6 border-t border-black/5">
                  <div className="font-serif-display text-lg">{t.name}</div>
                  <div className="text-sm text-[#6B705C]">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-2xl bg-[#0F4C5C] text-[#FDFBF7] grain p-10 md:p-16">
            <div className="grid md:grid-cols-12 gap-8 items-center relative">
              <div className="md:col-span-7">
                <div className="overline text-[#E3B23C]">Ready to Talk?</div>
                <h2 className="font-serif-display text-3xl md:text-5xl mt-3 leading-tight">
                  Let's help your school know what to do <em className="italic text-[#E3B23C]">next.</em>
                </h2>
                <p className="mt-5 text-[#FDFBF7]/80 max-w-xl leading-relaxed">
                  Schedule a TestFit demonstration, book Dr. Crystal for a keynote or explore a custom system for your institution.
                </p>
              </div>
              <div className="md:col-span-5 flex flex-col gap-3 md:items-end">
                <Link
                  to="/contact?interest=testfit_demo"
                  data-testid="closing-cta-demo"
                  className="btn-primary bg-[#E3B23C] border-[#E3B23C] text-[#22223B] hover:bg-[#d1a12f]"
                >
                  Schedule a Demonstration <ArrowUpRight size={16} />
                </Link>
                <Link
                  to="/contact?interest=speaking"
                  data-testid="closing-cta-speaking"
                  className="btn-secondary text-[#FDFBF7] border-[#FDFBF7]/60 hover:bg-white/10"
                >
                  Book Dr. Crystal to Speak
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
